import { useEffect, useState, useCallback, useMemo } from "react";
import { Col, Card, CardBody } from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import {
  deleteInvoice,
  getInvoices as fetchInvoices,
} from "../../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearInvoiceDetails,
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
  selectInvoicesState,
} from "slices/invoices/reducer";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import InvoiceListFilter from "./InvoiceListFilter";
import { getAvatarStyle, getInitials } from "utils/getAvatarColor";
import { getStatusBadge } from "utils/getStatusBatch";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";
import ActionMenu from "Components/Common/ActionMenu";
import { clearQuoteDetails } from "slices/quotes/reducer";
import { useAppSelector } from "Components/Hooks/hooks";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import { getCurrentWeekMonToSun } from "utils/utils";
import { IconName } from "Components/Icons/Icon";
import { ErrorToast } from "utils/toast";

export interface InvoiceListFilterState {
  startDate: string | null;
  endDate: string | null;
  status: string | null;
}

const InvoiceList = () => {
  const { state } = useLocation();
  const [selectInvoiceId, setSelectInvoiceId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);
  const user = useAppSelector((state) => state.auth.user);

  const goToInvoiceDetails = useCallback(
    (invoiceId: number) => {
      navigate("/invoice-details", { state: { id: invoiceId } });
    },
    [navigate],
  );

  const [filters, setFilters] = useState<InvoiceListFilterState>(
    state === 1
      ? { startDate: null, endDate: null, status: "overdue" }
      : state === 2
      ? {
          // ...getThisWeekDates(),
          ...getCurrentWeekMonToSun(),
          status: "due",
        }
      : {
          startDate: null,
          endDate: null,
          status: null,
        },
  );
  const handleFilterChange = (data: InvoiceListFilterState) => {
    setFilters(data);
    setShowFilter(false);
  };

  const invoiceSelector = createSelector(selectInvoicesState, (state) => ({
    invoiceItems: state.invoices,
    isLoading: state.status.fetch,
    error: state.errors.fetch,
    meta: state.pagination,
  }));

  const { invoiceItems, isLoading, meta } = useSelector(invoiceSelector);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;

  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => {},
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner.",
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  useEffect(() => {
    const params = searchTerm ? { search: searchTerm } : { page: currentPage };
    dispatch(
      fetchInvoices({
        ...params,
        start_date: filters.startDate,
        end_date: filters.endDate,
        status: filters.status,
      }),
    );
  }, [
    currentPage,
    dispatch,
    filters.endDate,
    filters.startDate,
    filters.status,
    searchTerm,
  ]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "INVOICE TITLE",
        accessorKey: "title",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "INVOICE",
        accessorKey: "reference_number",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "QUOTE",
        accessorKey: "quote_reference_number",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },

      {
        header: "CLIENT",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          let value = cell.getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="avatar-circle" style={getAvatarStyle(value)}>
                {getInitials(value)}
              </div>

              <div className="fw-normal text-black2D fs-14 font-primary">
                {value || "-"}
              </div>
            </div>
          );
        },
      },
      {
        header: "AMOUNT",
        accessorKey: "price",
        enableColumnFilter: false,
        cell: (cellProps: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {"£" + cellProps.getValue().toFixed(2)}
          </span>
        ),
      },
      {
        header: "STATUS",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cellProps: any) => getStatusBadge(cellProps?.getValue()),
      },
      {
        header: "CREATION DATE",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cellProps: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cellProps.getValue())}
          </span>
        ),
      },
      {
        header: "DUE DATE",
        accessorKey: "expiry_date",
        enableColumnFilter: false,
        cell: (cellProps: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cellProps.getValue())}
          </span>
        ),
      },
      {
        header: "ACTION",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionMenu
              onDelete={() => {
                setSelectInvoiceId(rowData.id);
                setDeleteModal(true);
              }}
              isView
              onView={() => goToInvoiceDetails(rowData.id)}
              onEdit={() => {
                if (hasNotSubscribed) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED,
                  );
                } else if (!hasBankAccount && !hasSignature) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
                  );
                } else if (!hasBankAccount) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED,
                  );
                } else if (!hasSignature) {
                  setShowSubscriptionExpiredPopUp(true);
                  setRestrictionModalData(
                    RESTRICTION_CONFIG.SIGNATURE_REQUIRED,
                  );
                } else {
                  // dispatch(clearItemState());
                  dispatch(clearQuoteDetails());
                  dispatch(clearInvoiceDetails());
                  dispatch(resetEditInvoiceStatus());
                  dispatch(resetCreateInvoiceStatus());
                  navigate("/manage-invoice", { state: { id: rowData.id } });
                }
              }}
              isEdit={rowData.is_editable}
              isDelete={rowData.is_editable}
            />
          );
        },
      },
    ],
    [
      RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED,
      RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
      RESTRICTION_CONFIG.SIGNATURE_REQUIRED,
      RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED,
      dispatch,
      goToInvoiceDetails,
      hasBankAccount,
      hasNotSubscribed,
      hasSignature,
      navigate,
    ],
  );

  return (
    <>
      <DeleteItemModal
        show={deleteModal}
        onCloseClick={() => setDeleteModal(false)}
        onDeleteClick={() => {
          if (selectInvoiceId !== null) {
            dispatch(deleteInvoice(selectInvoiceId));
            setTimeout(() => {
              dispatch(
                fetchInvoices({
                  search: searchTerm,
                  page: currentPage,
                  start_date: filters.startDate,
                  end_date: filters.endDate,
                  status: filters.status,
                }),
              );
            }, 1000);
          }
          setDeleteModal(false);
        }}
      />
      <Col xxl={12}>
        <Card id="invoiceList" className="br-10 bg-white">
          <CardBody className="pt-0">
            <TableContainer
              columns={columns}
              data={invoiceItems ?? []}
              isGlobalFilter={true}
              customPageSize={meta?.per_page}
              divClass="table-responsive table-card mb-2 bg-white"
              tableClass="align-middle table-nowrap table-borderless"
              thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
              theadClass="table-light"
              SearchPlaceholder="Search invoices & clients"
              onSearch={handleSearch}
              onPageChange={handlePageChange}
              currentPage={meta?.current_page}
              totalPages={meta?.last_page}
              toPage={meta?.to}
              fromPage={meta?.from}
              totalItems={meta?.total}
              isListFilter={true}
              onListFilter={() => setShowFilter(true)}
              isInitialLoading={
                isLoading === "loading" && !invoiceItems?.length
              }
              isPaginationLoading={
                isLoading === "loading" && invoiceItems?.length > 0
              }
            />
          </CardBody>
        </Card>
      </Col>
      <InvoiceListFilter
        filters={filters}
        show={showFilter}
        onCloseClick={() => setShowFilter(false)}
        onFilterChange={handleFilterChange}
      />
      <RestrictedAccessModal
        isOpen={showSubscriptionExpiredPopUp}
        onCancel={() => setShowSubscriptionExpiredPopUp(false)}
        iconName={restrictionModalData.iconName}
        title={restrictionModalData.title}
        description={restrictionModalData.description}
        buttonText={restrictionModalData.buttonText}
        onButtonClick={restrictionModalData.onAction}
      />
    </>
  );
};

export default InvoiceList;
